import { ReactNode, MouseEventHandler, useState } from 'react';
import cn from 'classnames';
import { Typography, Banner, Button, Spinner, Icon, TextArea } from '@scale/scaleui';
import { Transition } from '@headlessui/react';
import { makeStyles } from 'react-scale/components/design-system';
import {
  AutoCritiqueHook,
  AutoCritiqueSeverity,
  mapAutoCritiqueSeverityToVariant,
} from './hooks/useAutoCritique';
import { useTrans } from './hooks/useTrans';
import _ from 'lodash';
import { twMerge } from 'tailwind-merge';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';

export function ResetGlobalStyles({ children }: { children: ReactNode }) {
  const classes = useStyles();
  return <div className={classes.reset}>{children}</div>;
}

export const AutoCritiqueButton = ({
  onClick,
  loading,
  className,
  buttonLabel,
  disabled,
}: {
  onClick: MouseEventHandler<HTMLButtonElement>;
  loading: boolean;
  className?: string;
  buttonLabel?: string;
  disabled?: boolean;
}) => {
  const { t } = useTrans();

  return (
    <Button
      className={cn(className, {
        'pointer-events-none': loading,
      })}
      variant="secondary"
      onClick={onClick}
      disabled={disabled}
    >
      {loading ? (
        <>
          <Spinner className="mr-1" size="xs" />
          {buttonLabel ? 'Fetching ' + buttonLabel : t('fetching-auto-critique')}
        </>
      ) : (
        <>{buttonLabel ? 'Get ' + buttonLabel : t('get-auto-critique')} </>
      )}
    </Button>
  );
};

export const AutoCritiqueAlert = ({
  autoCritiqueHook,
  headerLabel,
}: {
  autoCritiqueHook: AutoCritiqueHook;
  headerLabel?: string;
}) => {
  const { severity, critique, loading } = autoCritiqueHook.critiqueData;
  if (severity === AutoCritiqueSeverity.Missing && loading) {
    return null;
  }

  return (
    <Transition
      show={!!critique}
      enter="transition-opacity duration-75"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-150"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <Banner
        title={headerLabel || 'Critique'}
        subtitle={critique}
        variant={mapAutoCritiqueSeverityToVariant(severity)}
        background="neutral"
        className="my-2 whitespace-pre-wrap"
      />
    </Transition>
  );
};

const useStyles = makeStyles({
  reset: {
    '& p': {
      lineHeight: 1.5,
    },
  },
});

export const Alert = ({
  variant,
  className,
  children,
}: {
  variant: 'info' | 'warning' | 'danger';
  className?: string;
  children: ReactNode;
}) => {
  const variantToIcon: Record<'info' | 'warning' | 'danger', IconName> = {
    info: 'info-circle',
    warning: 'exclamation-triangle',
    danger: 'exclamation-circle',
  };
  return (
    <div
      className={twMerge(
        'flex flex-row items-center gap-2 transition-[opacity] duration-500 rounded p-1 w-full',
        `bg-${variant}-50`,
        className,
      )}
    >
      <Icon hasBackground variant={variant} icon={['far', variantToIcon[variant]]} size="lg" />
      <div>{children}</div>
    </div>
  );
};

export function CardAlert({ message, children }: { message?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      {!_.isUndefined(message) && (
        <div
          className={twMerge(
            'flex flex-row items-center gap-2 bg-warning-50 transition-[opacity] duration-500 rounded p-2',
            !_.isUndefined(message) ? 'w-full shadow-[2px_2px_15px_0px_rgba(0,0,0,0.5)]' : 'w-0',
          )}
        >
          <Icon hasBackground variant="warning" icon={['far', 'exclamation-triangle']} size="lg" />
          <Typography.Paragraph1>{message}</Typography.Paragraph1>
        </div>
      )}
      <div
        className={
          !_.isUndefined(message) ? 'flex flex-col gap-4 bg-warning-50 p-4' : 'flex flex-col gap-4'
        }
        style={
          !_.isUndefined(message)
            ? {
                boxShadow: '2px 2px 15px 0px rgba(0,0,0,0.5)',
                transition: 'all 500ms',
              }
            : {}
        }
      >
        {children}
      </div>
    </div>
  );
}

interface LaTeXEditorProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  initialText: any;
  onChange?: (newValue: any) => void;
  readOnly: boolean;
  placeholder: any;
  enableSingleDollarTextMath: boolean;
  direction?: 'row' | 'col';
  children?: React.ReactNode;
  disablePasting?: boolean;
  setShowDisablePastingAlert?: (show: boolean) => void;
}

/*
 * Used for Process Supervision and prompt components (EditableChatMessage, InteractionInput) that require LaTeX
 * TODO: This should be deprecated at some point
 */
export const LaTeXEditor: React.FC<LaTeXEditorProps> = ({
  initialText,
  onChange,
  readOnly,
  placeholder,
  enableSingleDollarTextMath,
  direction = 'row',
  children,
  disablePasting,
  setShowDisablePastingAlert,
}) => {
  const [showPreview, setShowPreview] = useState<boolean>(true);

  return (
    <div className="w-full">
      <div className={`flex flex-${direction} gap-4 w-full`}>
        {children || (
          <TextArea
            placeholder={placeholder}
            value={initialText}
            disabled={readOnly}
            onChange={(e: any) => {
              const text = e.target.value;
              onChange?.(text);
            }}
            onPaste={e => {
              if (disablePasting) {
                e.preventDefault();
                setShowDisablePastingAlert?.(true);
              }
            }}
            onDragOver={e => {
              if (disablePasting) {
                e.preventDefault();
              }
            }}
            onDrop={e => {
              if (disablePasting) {
                e.preventDefault();
                setShowDisablePastingAlert?.(true);
              }
            }}
            className="flex-1 w-1/2"
            textAreaWrapperClassName="flex-1"
          />
        )}
        {showPreview && (
          <div className="w-1/2 border border-neutral-200 rounded-lg p-2">
            <ChatMarkdown
              markdown={initialText}
              enableMath={true}
              enableSingleDollarTextMath={enableSingleDollarTextMath}
            />
          </div>
        )}
      </div>
      <div className="w-full text-right text-xs mt-2 text-neutral-500">
        <button
          onClick={e => {
            e.preventDefault();
            setShowPreview(!showPreview);
          }}
        >
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </button>
      </div>
    </div>
  );
};
